import { NOTIFY_CHANNELS, NOTIFY_CHANNEL_LABELS, NOTIFY_KINDS, NOTIFY_KIND_INFO, type NotifyChannel, type NotifyKind, type NotifySection, type NotifySettingsView } from "@nmt/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { errorMessage, get, put } from "../api";
import { Alert, Checkbox, Loading, Panel, PanelHeader, cx } from "./ui";

type Prefs = NotifySettingsView["prefs"];

/**
 * Which notifications go by push, text, and email: for your own things and jobs, and (managers) for
 * your team's or everyone's jobs. `url` is your own (/api/push/prefs) or, for admins, a person's.
 */
export function NotifyGrid({ url, admin = false, name }: { url: string; admin?: boolean; name?: string }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["notify-settings", url], queryFn: () => get<NotifySettingsView>(url) });
  const save = useMutation({
    mutationFn: (prefs: Prefs) => put<NotifySettingsView>(url, prefs),
    onMutate: (prefs) => {
      // Shows the change right away.
      const cur = qc.getQueryData<NotifySettingsView>(["notify-settings", url]);
      if (cur) qc.setQueryData(["notify-settings", url], { ...cur, prefs });
    },
    onSuccess: (d) => qc.setQueryData(["notify-settings", url], d),
    onError: () => void qc.invalidateQueries({ queryKey: ["notify-settings", url] }),
  });
  if (q.isLoading) return <Loading />;
  if (q.error) return <Alert>{errorMessage(q.error)}</Alert>;
  const v = q.data!;
  const p = v.prefs;
  const ro = v.readOnly || save.isPending;
  const set = (section: NotifySection, kind: NotifyKind, channel: NotifyChannel, on: boolean) =>
    save.mutate({ ...p, [section]: { ...p[section], [kind]: { ...p[section][kind], [channel]: on } } });
  const why = (c: NotifyChannel) => (c === "text" && !v.textOn ? "Texting isn't set up here." : c === "text" && !v.phone ? `Add a mobile number to ${admin ? "their" : "your"} profile to get texts.` : c === "email" && !v.emailOn ? "Email isn't on here yet." : null);
  const notes = NOTIFY_CHANNELS.map(why).filter(Boolean);
  const jobKinds = NOTIFY_KINDS.filter((k) => NOTIFY_KIND_INFO[k].job || k === "job_assigned");

  const grid = (section: NotifySection, kinds: readonly NotifyKind[]) => (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-mist text-left text-xs font-semibold tracking-wide text-muted uppercase">
            <th className="px-5 py-2 font-semibold">{section === "mine" ? "What" : "On their jobs"}</th>
            {NOTIFY_CHANNELS.map((c) => (
              <th key={c} className="w-16 px-2 py-2 text-center font-semibold">
                {NOTIFY_CHANNEL_LABELS[c]}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {kinds.map((k) => (
            <tr key={k} className="border-b border-mist last:border-0">
              <td className="px-5 py-2.5">
                <div className="font-medium text-ink">{section === "others" && k === "job_assigned" ? "Job given to one of them" : NOTIFY_KIND_INFO[k].label}</div>
                <div className="text-xs text-muted">{section === "others" && k === "job_assigned" ? "When someone's made a job's rep or added as another rep." : NOTIFY_KIND_INFO[k].description}</div>
              </td>
              {NOTIFY_CHANNELS.map((c) => (
                <td key={c} className="px-2 py-2.5 text-center">
                  <input
                    type="checkbox"
                    className="size-4 accent-[var(--color-brand)] disabled:opacity-40"
                    aria-label={`${NOTIFY_KIND_INFO[k].label}: ${NOTIFY_CHANNEL_LABELS[c]}`}
                    checked={p[section][k][c]}
                    disabled={ro || (!!why(c) && !p[section][k][c])}
                    onChange={(e) => set(section, k, c, e.target.checked)}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="flex flex-col gap-5">
      {save.error && <Alert>{errorMessage(save.error)}</Alert>}
      {v.readOnly && <Alert tone="info">An admin set your notifications. Ask them to change them.</Alert>}
      <Panel>
        <PanelHeader title={admin ? `${name ?? "Their"} notifications` : "What to send, and how"} description={`Push goes to every phone and computer with notifications turned on. Texts come from the NMT number${v.phone ? ` to ${v.phone}` : ""}; emails to ${v.email}.`} />
        {notes.length > 0 && <p className="px-5 pt-3 text-xs text-muted">{notes.join(" ")}</p>}
        {grid("mine", NOTIFY_KINDS)}
      </Panel>
      {v.canTeam && (
        <Panel>
          <PanelHeader
            title={p.othersScope === "everyone" ? "Everyone's jobs" : "My team's jobs"}
            description={p.othersScope === "everyone" ? "Every job in the company (not your own: those are above)." : "Jobs of the people who report to you, and to them (Settings → Users → Reports to)."}
            action={
              v.canEveryone && (
                <div className="inline-flex rounded-[var(--radius-control)] bg-mist p-0.5 text-sm">
                  {(["team", "everyone"] as const).map((s) => (
                    <button key={s} type="button" disabled={ro} onClick={() => save.mutate({ ...p, othersScope: s })} className={cx("rounded-[6px] px-2.5 py-1 font-semibold", p.othersScope === s ? "bg-white shadow-sm" : "text-muted hover:text-ink")}>
                      {s === "team" ? "My team" : "Everyone"}
                    </button>
                  ))}
                </div>
              )
            }
          />
          {grid("others", jobKinds)}
        </Panel>
      )}
      <Panel>
        <PanelHeader title="Quiet hours for texts" description="Texts that come up in these hours wait until they end. Push and email still come right away." />
        <div className="flex flex-wrap items-end gap-3 px-5 py-4 text-sm">
          {(["quietStart", "quietEnd"] as const).map((f) => (
            <label key={f} className="flex flex-col gap-1">
              <span className="font-semibold text-ink-soft">{f === "quietStart" ? "From" : "Until"}</span>
              <input type="time" className="rounded-[var(--radius-control)] border border-line px-3 py-1.5 text-base sm:text-[15px]" value={p[f]} disabled={ro} onChange={(e) => e.target.value && save.mutate({ ...p, [f]: e.target.value })} />
            </label>
          ))}
          <span className="pb-2 text-xs text-muted">Same time for both: no quiet hours.</span>
        </div>
      </Panel>
      {admin && (
        <Panel>
          <div className="px-3 py-2">
            <Checkbox label="Lock these" description="They can see their notifications but not change them." checked={p.locked} disabled={save.isPending} onChange={(locked) => save.mutate({ ...p, locked })} />
          </div>
        </Panel>
      )}
    </div>
  );
}
